import {
  type ApolloClient,
  gql,
  type NormalizedCacheObject,
} from "@apollo/client";
import type { AtomInfos, CONTEXT_KEY, EntityInfos } from "@atoms/atom-types";
import type { Delta } from "jsondiffpatch";
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AtomStore } from "./AtomStore";
import { useAuthToken } from "./auth/AuthProvider";
import { useBackendConnection } from "./connection/backendConnectionStore";
import { getFrameworkInnerWrapper } from "./FrameworkInnerWrapperRegistry";
import { initApolloClient2 } from "./initApolloClient";
import type { SelectorFunction } from "./use-atom/makeUseAtom";

export const subscriptionDocument = gql`
  subscription subscribeAtom($type: String!, $context: JSON!, $parameters: JSON) {
    subscribeAtom(type: $type, context: $context, parameters: $parameters)
  }
`;

export type SubscriptionCallback<RType extends object = object> = (
  value: RType | null,
  patches: Delta | undefined,
  computedPatches?: boolean,
) => void;

/**
 * Called when the subscription of an atom failed: the server rejected it, or
 * the connection was lost before a first value arrived.
 */
export type SubscriptionErrorCallback = (error: Error) => void;

export type SubscriptionProps<RType extends object = object> = {
  current: Promise<ZenObservable.Subscription>;
  /** after the last subscriber is removed, the websocket subscription remains open for a second to see if something is resubscribed */
  unsubscribeTimeout?: ReturnType<typeof setTimeout>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  selectors: Array<SelectorFunction<any, any>>;
  callbacks: Array<SubscriptionCallback<RType>>;
  /** The error callback a value callback was subscribed with, if any. */
  errorCallbacks?: Map<unknown, SubscriptionErrorCallback>;
  lastValue: RType | null | undefined;
  /**
   * The subscription ended while the entry keeps its state: it is reopened
   * on the next reconnect.
   */
  completed?: boolean;
  /** Why the subscription failed; `getAtom` throws it. */
  error?: Error;
  atomType?: string;
  context?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
};

export type SubscriptionCallbacks = { [path: string]: SubscriptionProps };

export const AtomStoreContext = createContext<AtomStore | null>(null);

interface AtomStoreProviderProps {
  children: ReactNode;
  atomInfosExtended: AtomInfos<string, string>;
  entityInfos: EntityInfos<string, CONTEXT_KEY<string>>;
}
export const AtomStoreProvider = ({
  children,
  atomInfosExtended,
  entityInfos,
}: AtomStoreProviderProps) => {
  const getAuthToken = useAuthToken();
  const getAuthTokenRef = useRef(getAuthToken);
  getAuthTokenRef.current = getAuthToken;

  // Keep Apollo/WebSocket client lifetime independent from auth getter identity.
  // Auth modules (e.g. Better Auth) replace the getter after session hydration;
  // recreating the client there races active subscriptions and leaves hooks loading.
  const stableGetAuthToken = useCallback(
    (): Promise<string | null> => getAuthTokenRef.current(),
    [],
  );

  const { isConnected } = useBackendConnection();
  const [apolloClient, setApolloClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);

  useEffect(() => {
    const { apolloClient, wsLink } = initApolloClient2(stableGetAuthToken);
    setApolloClient(apolloClient);

    return () => {
      apolloClient?.stop();
      wsLink.client.dispose();
    };
  }, [stableGetAuthToken]);

  // Important: keep hook order stable across renders.
  // On the first render, apolloClient is null; we still must call useMemo.
  const atomStore = useMemo(() => {
    if (!apolloClient) return null;
    return new AtomStore(apolloClient, atomInfosExtended, entityInfos);
  }, [apolloClient, atomInfosExtended, entityInfos]);

  // On reconnect, reopen subscriptions to avoid missing intermediate updates.
  useEffect(() => {
    if (!atomStore) return;
    if (!isConnected) return;
    void atomStore.resubscribeAll();
  }, [atomStore, isConnected]);

  if (!atomStore) {
    return null;
  }

  const InnerWrapper = getFrameworkInnerWrapper();
  const content = InnerWrapper ? (
    <InnerWrapper>{children}</InnerWrapper>
  ) : (
    children
  );

  return (
    <AtomStoreContext.Provider value={atomStore}>
      {content}
    </AtomStoreContext.Provider>
  );
};
